/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Which version of a shared expression a row recorded, and whether it has moved since** — `#1302`,
 * ratified by Todd 2026-09-10.
 *
 * Its own module, and not because it is large. It is read at **both ends** — the writer that stamps
 * a version onto a reference (`value-line.ts`) and the readers that decide whether a row is behind
 * (`mapping-value-round-trip.ts`, and the value line's own mark) — and those two files already point
 * one way at each other. A rule that lives in either of them is a rule the other reaches through a
 * cycle, which is the shape that makes a hoisting accident look like a missing mark.
 *
 * The failure it exists to prevent is `#1269`'s: *one rule, two implementations*, where the narrower
 * one is a **reader** and its output is **nothing**. Nobody reports nothing.
 */

import type { ReferenceExpression } from '../../../types/decorator-expression';

/** The two stamps a library entry can be versioned by, in the order they answer. */
export interface LibraryVersion {
  updatedAt?: string;
  createdAt?: string;
}

/**
 * **Which version of a library entry a row records** (`#1302`, ratified by Todd 2026-09-10).
 *
 * One function, called by the **writer** — picking an expression, and revisiting one — and by the
 * **reader** that decides whether a row is behind. Two implementations of one rule is `#1269`, and
 * that defect is invisible in exactly this direction: a narrower reader produces *no mark*, which
 * looks like nothing has changed.
 *
 * `createdAt` answers where an entry has never been edited, so a row picked before the first edit
 * still has a baseline to be measured against. `undefined` is a real answer for an entry carrying
 * neither, and it makes the row silent rather than making it wrong.
 */
export function entryVersion(entry: LibraryVersion | undefined): string | undefined {
  return entry?.updatedAt ?? entry?.createdAt;
}

/**
 * **Has the shared expression this row addresses moved since the row recorded it?**
 *
 * Three things it must never say, and each is a state it can actually be in:
 *
 * - **`false` where nothing was recorded.** Absence is *nobody recorded a version*, so an unstamped
 *   row is silent. Treating it as *changed* would mark every row authored before this existed.
 * - **`false` where the entry cannot be read.** A missing or still-loading entry has no version, and
 *   a mark then would be a claim built on a request in flight — `SharedReadOut`'s rule.
 * - **Inequality, never *newer*.** The claim is *this is not the version you recorded*, which stays
 *   true when an entry is restored to an older one and needs no opinion about clocks.
 */
export function referenceIsBehind(reference: ReferenceExpression, current: string | undefined): boolean {
  if (!reference.seenAt || !current) return false;
  return reference.seenAt !== current;
}

/**
 * **The key a row records the version under — a record of attention, never authored content.**
 *
 * Opening a row writes it (`#1302`), and nobody typed anything, so it must never count as unsaved
 * work: a discard prompt for a change the operator did not make is how they learn to click through
 * the prompt that matters (Todd, 2026-09-14). It still persists with whatever the operator next
 * saves, which is what finally clears the mark.
 */
export const RECORDED_VERSION_KEY = 'seenAt';

/** A `JSON.stringify` replacer that leaves recorded versions out, for comparisons of unsaved work. */
export function withoutRecordedVersions(key: string, value: unknown): unknown {
  return key === RECORDED_VERSION_KEY ? undefined : value;
}

/** Record the version now in force — what revisiting a changed expression writes back (`#1302`). */
export function withReferenceSeenAt(reference: ReferenceExpression, version: string | undefined): ReferenceExpression {
  return version ? { ...reference, seenAt: version } : reference;
}
